import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { feedbackAPI } from "../services/api";
import { Feedback } from "../types";
import { MessageSquare, Frown, Smile, Meh } from "lucide-react";

const Feedbacks: React.FC = () => {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const location = useLocation();
  const [ratingFilter, setRatingFilter] = useState<"all" | "positive" | "neutral" | "negative">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const guestId = new URLSearchParams(location.search).get("guestId") || undefined;

  useEffect(() => {
    fetchFeedback();
  }, [guestId]);

  const fetchFeedback = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await feedbackAPI.list(guestId);
      setFeedback(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const iconFor = (rating: string) => {
    if (rating === "positive") return <Smile className="h-4 w-4 text-green-600" />;
    if (rating === "negative") return <Frown className="h-4 w-4 text-red-600" />;
    return <Meh className="h-4 w-4 text-amber-600" />;
  };

  const filtered = feedback.filter((fb) => {
    const matchesRating = ratingFilter === "all" ? true : fb.rating === ratingFilter;
    const matchesSearch =
      fb.guest_name?.toLowerCase().includes(search.toLowerCase()) ||
      fb.room_number?.toLowerCase().includes(search.toLowerCase()) ||
      fb.comment?.toLowerCase().includes(search.toLowerCase());
    return matchesRating && (matchesSearch || search.trim() === "");
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-black-guestby flex items-center space-x-2">
          <MessageSquare className="h-7 w-7 text-blue-guestby" />
          <span>Guest Feedback</span>
        </h1>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48 text-grey-guestby">Loading feedback...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-grey-guestby">Filter:</span>
              <select
                value={ratingFilter}
                onChange={(e) => {
                  setRatingFilter(e.target.value as any);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
              >
                <option value="all">All ratings</option>
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
              </select>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search guest, room, comment"
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby w-full md:w-64"
            />
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Guest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Room
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-6 text-center text-sm text-grey-guestby">
                    No feedback yet.
                  </td>
                </tr>
              )}
              {paginated.map((fb) => (
                <tr key={fb.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-black-guestby">
                    {fb.guest_name || "Guest"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-guestby">
                    {fb.room_number || "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                    <span className="inline-flex items-center space-x-2">
                      {iconFor(fb.rating)}
                      <span className="capitalize">{fb.rating}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-pre-wrap text-sm text-black-guestby">
                    {fb.comment || "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-guestby">
                    {new Date(fb.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-4 flex items-center justify-between">
            <div className="text-sm text-grey-guestby">
              Page {currentPage} of {totalPages}
            </div>
            <div className="space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedbacks;

